/**
 * Admin IT Services Dashboard
 * Manage IT career services: providers, triggers, settings, and subscriptions
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Briefcase,
  FileText,
  MessageSquare,
  GraduationCap,
  Building2,
  Settings,
  Users,
  Star,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Loader2,
  Target,
  TrendingUp,
  DollarSign,
  Globe,
  Check,
  X,
  Zap,
  Eye,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'SL', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪' },
];

const SERVICE_TYPES = [
  { id: 'cv_review', name: 'CV Review', icon: '👩‍💻', category: 'candidate' },
  { id: 'interview_prep', name: 'Interview Prep', icon: '💬', category: 'candidate' },
  { id: 'coding_mentorship', name: 'Coding Mentorship', icon: '🧑‍🏫', category: 'candidate' },
  { id: 'salary_negotiation', name: 'Salary Negotiation', icon: '💰', category: 'candidate' },
  { id: 'remote_job_readiness', name: 'Remote Readiness', icon: '🏠', category: 'candidate' },
  { id: 'candidate_screening', name: 'Candidate Screening', icon: '🏢', category: 'employer' },
  { id: 'talent_matching', name: 'Talent Matching', icon: '🎯', category: 'employer' },
  { id: 'job_promotion', name: 'Job Promotion', icon: '📣', category: 'employer' },
];

const AdminITServices = () => {
  const [selectedCountry, setSelectedCountry] = useState('UK');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [settings, setSettings] = useState(null);
  
  // Dialog states
  const [showProviderDialog, setShowProviderDialog] = useState(false);
  const [showTriggerDialog, setShowTriggerDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [editingTrigger, setEditingTrigger] = useState(null);
  
  const { toast } = useToast();

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, providersRes, triggersRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/it-services/admin/stats`, { params: { country: selectedCountry } }),
        axios.get(`${API_URL}/api/it-services/admin/providers`, { params: { country: selectedCountry } }),
        axios.get(`${API_URL}/api/it-services/admin/triggers/${selectedCountry}`),
        axios.get(`${API_URL}/api/it-services/admin/settings/${selectedCountry}`),
      ]);
      
      setStats(statsRes.data.stats);
      setProviders(providersRes.data.providers || []);
      setTriggers(triggersRes.data.triggers || []);
      setSettings(settingsRes.data.settings);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load IT services data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [selectedCountry, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Seed demo providers
  const handleSeedProviders = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/it-services/seed-providers`,
        {},
        { params: { country: selectedCountry } }
      );
      toast({
        title: 'Providers Seeded',
        description: response.data.message,
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to seed providers',
        variant: 'destructive',
      });
    }
  };

  // Toggle provider status
  const handleToggleProvider = async (providerId) => {
    try {
      const response = await axios.put(`${API_URL}/api/it-services/admin/providers/${providerId}/toggle`);
      toast({
        title: response.data.is_active ? 'Provider Activated' : 'Provider Deactivated',
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle provider status',
        variant: 'destructive',
      });
    }
  };

  // Delete provider
  const handleDeleteProvider = async (providerId) => {
    if (!window.confirm('Are you sure you want to delete this provider?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/it-services/admin/providers/${providerId}`);
      toast({ title: 'Provider Deleted' });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete provider',
        variant: 'destructive',
      });
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    try {
      await axios.post(
        `${API_URL}/api/it-services/admin/settings/${selectedCountry}`,
        settings
      );
      toast({ title: 'Settings Saved' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      });
    }
  };

  // Update trigger
  const handleSaveTrigger = async (triggerConfig) => {
    try {
      await axios.post(
        `${API_URL}/api/it-services/admin/triggers/${selectedCountry}`,
        triggerConfig
      );
      toast({ title: 'Trigger Updated' });
      setShowTriggerDialog(false);
      setEditingTrigger(null);
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update trigger',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading IT Services...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-it-services">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-blue-600" />
            IT Career Services
          </h1>
          <p className="text-gray-500 mt-1">
            Manage IT career services, providers, and configurations
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Country Selector */}
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  <span className="flex items-center gap-2">
                    <span>{country.flag}</span>
                    <span>{country.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Providers</p>
                <p className="text-2xl font-bold">{stats?.total_providers || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Providers</p>
                <p className="text-2xl font-bold text-green-600">{stats?.active_providers || 0}</p>
              </div>
              <Check className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Bookings</p>
                <p className="text-2xl font-bold">{stats?.total_bookings || 0}</p>
              </div>
              <Target className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Bookings</p>
                <p className="text-2xl font-bold text-amber-600">{stats?.pending_bookings || 0}</p>
              </div>
              <Zap className="h-8 w-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Subscriptions</p>
                <p className="text-2xl font-bold text-blue-600">{stats?.active_subscriptions || 0}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="triggers">Triggers</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Service Types Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Service Types</CardTitle>
                <CardDescription>Available IT career services</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-gray-500">Candidate Services</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {SERVICE_TYPES.filter(s => s.category === 'candidate').map((service) => (
                      <div key={service.id} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                        <span>{service.icon}</span>
                        <span className="text-sm">{service.name}</span>
                      </div>
                    ))}
                  </div>
                  
                  <h4 className="font-medium text-sm text-gray-500 mt-4">Employer Services</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {SERVICE_TYPES.filter(s => s.category === 'employer').map((service) => (
                      <div key={service.id} className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
                        <span>{service.icon}</span>
                        <span className="text-sm">{service.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => {
                    setEditingProvider(null);
                    setShowProviderDialog(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Provider
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleSeedProviders}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Seed Demo Providers
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setActiveTab('triggers')}
                >
                  <Target className="h-4 w-4 mr-2" />
                  Configure Triggers
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setActiveTab('settings')}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Country Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Providers Tab */}
        <TabsContent value="providers" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">IT Service Providers</h3>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSeedProviders}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Seed Demo
              </Button>
              <Button onClick={() => {
                setEditingProvider(null);
                setShowProviderDialog(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Provider
              </Button>
            </div>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead>Services</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Price Range</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No providers found. Click &quot;Seed Demo&quot; to add sample providers.
                    </TableCell>
                  </TableRow>
                ) : (
                  providers.map((provider) => (
                    <TableRow key={provider.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{provider.name}</p>
                          <p className="text-sm text-gray-500 truncate max-w-xs">
                            {provider.description?.substring(0, 50)}...
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {provider.service_types?.slice(0, 2).map((type) => (
                            <Badge key={type} variant="outline" className="text-xs">
                              {SERVICE_TYPES.find(s => s.id === type)?.icon || ''}
                            </Badge>
                          ))}
                          {provider.service_types?.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{provider.service_types.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          <span>{provider.rating}</span>
                          <span className="text-gray-400 text-sm">({provider.reviews_count})</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {provider.currency === 'GBP' ? '£' : provider.currency === 'LKR' ? 'Rs' : '$'}
                        {provider.price_min}
                        {provider.price_max && ` - ${provider.price_max}`}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={provider.is_active}
                          onCheckedChange={() => handleToggleProvider(provider.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setEditingProvider(provider);
                              setShowProviderDialog(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteProvider(provider.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Triggers Tab */}
        <TabsContent value="triggers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Service Triggers</CardTitle>
              <CardDescription>
                Configure when IT career services are suggested to users
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-gray-500">Candidate Triggers</h4>
                {triggers.filter(t => t.trigger_type?.startsWith('view') || t.trigger_type?.includes('job') || t.trigger_type?.includes('role')).map((trigger) => (
                  <div key={trigger.trigger_type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{trigger.trigger_type?.replace(/_/g, ' ')}</p>
                      <p className="text-sm text-gray-500">{trigger.message}</p>
                      <div className="flex gap-1 mt-1">
                        {trigger.services?.map((s) => (
                          <Badge key={s} variant="outline" className="text-xs">
                            {SERVICE_TYPES.find(st => st.id === s)?.name || s}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={trigger.is_active !== false} />
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setEditingTrigger(trigger);
                          setShowTriggerDialog(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Country Settings - {COUNTRIES.find(c => c.code === selectedCountry)?.name}</CardTitle>
              <CardDescription>
                Enable/disable services and configure pricing for this country
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Candidate Services */}
              <div>
                <h4 className="font-medium mb-3">Candidate Services</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>👩‍💻</span>
                      <span>CV Review</span>
                    </div>
                    <Switch
                      checked={settings?.cv_review_enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, cv_review_enabled: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>💬</span>
                      <span>Interview Prep</span>
                    </div>
                    <Switch
                      checked={settings?.interview_prep_enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, interview_prep_enabled: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>🧑‍🏫</span>
                      <span>Mentorship</span>
                    </div>
                    <Switch
                      checked={settings?.mentorship_enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, mentorship_enabled: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>💰</span>
                      <span>Salary Negotiation</span>
                    </div>
                    <Switch
                      checked={settings?.salary_negotiation_enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, salary_negotiation_enabled: v })}
                    />
                  </div>
                </div>
              </div>

              {/* Employer Services */}
              <div>
                <h4 className="font-medium mb-3">Employer Services</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>🏢</span>
                      <span>Candidate Screening</span>
                    </div>
                    <Switch
                      checked={settings?.candidate_screening_enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, candidate_screening_enabled: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>🎯</span>
                      <span>Talent Matching</span>
                    </div>
                    <Switch
                      checked={settings?.talent_matching_enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, talent_matching_enabled: v })}
                    />
                  </div>
                </div>
              </div>

              {/* Pricing Settings */}
              <div>
                <h4 className="font-medium mb-3">Pricing Configuration</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="multiplier">Pricing Multiplier</Label>
                    <Input
                      id="multiplier"
                      type="number"
                      step="0.1"
                      value={settings?.pricing_multiplier || 1}
                      onChange={(e) => setSettings({ ...settings, pricing_multiplier: parseFloat(e.target.value) })}
                    />
                    <p className="text-xs text-gray-500 mt-1">Multiply base prices by this factor</p>
                  </div>
                  <div>
                    <Label htmlFor="tax">Tax Rate (%)</Label>
                    <Input
                      id="tax"
                      type="number"
                      step="0.5"
                      value={settings?.tax_rate || 0}
                      onChange={(e) => setSettings({ ...settings, tax_rate: parseFloat(e.target.value) })}
                    />
                    <p className="text-xs text-gray-500 mt-1">VAT/GST rate for this country</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveSettings}>
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Provider Dialog */}
      <ProviderDialog
        open={showProviderDialog}
        onClose={() => {
          setShowProviderDialog(false);
          setEditingProvider(null);
        }}
        provider={editingProvider}
        country={selectedCountry}
        onSave={() => {
          fetchData();
          setShowProviderDialog(false);
          setEditingProvider(null);
        }}
      />

      {/* Trigger Dialog */}
      <TriggerDialog
        open={showTriggerDialog}
        onClose={() => {
          setShowTriggerDialog(false);
          setEditingTrigger(null);
        }}
        trigger={editingTrigger}
        onSave={handleSaveTrigger}
      />
    </div>
  );
};


// Provider Dialog Component
const ProviderDialog = ({ open, onClose, provider, country, onSave }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    service_types: [],
    rating: 4.5,
    reviews_count: 0,
    price_min: 25,
    price_max: 100,
    experience_years: 5,
    specializations: [],
    available_online: true,
    available_inperson: false,
    location: '',
    is_sponsored: false,
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (provider) {
      setForm({
        name: provider.name || '',
        description: provider.description || '',
        service_types: provider.service_types || [],
        rating: provider.rating || 4.5,
        reviews_count: provider.reviews_count || 0,
        price_min: provider.price_min || 25,
        price_max: provider.price_max || 100,
        experience_years: provider.experience_years || 5,
        specializations: provider.specializations || [],
        available_online: provider.available_online !== false,
        available_inperson: provider.available_inperson || false,
        location: provider.location || '',
        is_sponsored: provider.is_sponsored || false,
      });
    } else {
      setForm({
        name: '',
        description: '',
        service_types: [],
        rating: 4.5,
        reviews_count: 0,
        price_min: 25,
        price_max: 100,
        experience_years: 5,
        specializations: [],
        available_online: true,
        available_inperson: false,
        location: '',
        is_sponsored: false,
      });
    }
  }, [provider]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (provider) {
        await axios.put(`${API_URL}/api/it-services/admin/providers/${provider.id}`, form);
        toast({ title: 'Provider Updated' });
      } else {
        await axios.post(`${API_URL}/api/it-services/admin/providers`, form, { params: { country } });
        toast({ title: 'Provider Created' });
      }
      onSave();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save provider',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{provider ? 'Edit Provider' : 'Add New Provider'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Provider Name *</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              required
            />
          </div>
          
          <div>
            <Label>Service Types *</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {SERVICE_TYPES.map((service) => (
                <label key={service.id} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={form.service_types.includes(service.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setForm({ ...form, service_types: [...form.service_types, service.id] });
                      } else {
                        setForm({ ...form, service_types: form.service_types.filter(s => s !== service.id) });
                      }
                    }}
                  />
                  <span>{service.icon}</span>
                  <span className="text-sm">{service.name}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="price_min">Min Price</Label>
              <Input
                id="price_min"
                type="number"
                value={form.price_min}
                onChange={(e) => setForm({ ...form, price_min: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="price_max">Max Price</Label>
              <Input
                id="price_max"
                type="number"
                value={form.price_max}
                onChange={(e) => setForm({ ...form, price_max: parseFloat(e.target.value) })}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="rating">Rating</Label>
              <Input
                id="rating"
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="experience">Experience (years)</Label>
              <Input
                id="experience"
                type="number"
                value={form.experience_years}
                onChange={(e) => setForm({ ...form, experience_years: parseInt(e.target.value) })}
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.available_online}
                onChange={(e) => setForm({ ...form, available_online: e.target.checked })}
              />
              <span className="text-sm">Available Online</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_sponsored}
                onChange={(e) => setForm({ ...form, is_sponsored: e.target.checked })}
              />
              <span className="text-sm">Sponsored</span>
            </label>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {provider ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};


// Trigger Dialog Component
const TriggerDialog = ({ open, onClose, trigger, onSave }) => {
  // Initialize form directly from trigger prop to avoid useEffect setState issues
  const getInitialForm = () => ({
    trigger_type: trigger?.trigger_type || '',
    services: trigger?.services || [],
    message: trigger?.message || '',
    is_active: trigger?.is_active !== false,
  });
  
  const [form, setForm] = useState(getInitialForm);

  // Reset form when trigger changes using key prop pattern
  const formKey = trigger?.trigger_type || 'new';

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      trigger_type: trigger?.trigger_type || form.trigger_type,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose} key={formKey}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Trigger: {trigger?.trigger_type?.replace(/_/g, ' ')}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="message">Display Message</Label>
            <Input
              id="message"
              value={form.message || trigger?.message || ''}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </div>
          
          <div>
            <Label>Services to Suggest</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {SERVICE_TYPES.map((service) => {
                const currentServices = form.services?.length ? form.services : (trigger?.services || []);
                return (
                  <label key={service.id} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={currentServices.includes(service.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setForm({ ...form, services: [...currentServices, service.id] });
                        } else {
                          setForm({ ...form, services: currentServices.filter(s => s !== service.id) });
                        }
                      }}
                    />
                    <span>{service.icon}</span>
                    <span className="text-sm">{service.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              checked={form.is_active !== undefined ? form.is_active : (trigger?.is_active !== false)}
              onCheckedChange={(v) => setForm({ ...form, is_active: v })}
            />
            <Label>Trigger Active</Label>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              Save Trigger
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminITServices;
